import { Box, IconButton, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FileSpreadsheet, Repeat } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { CategoryPickerPanel } from "../../components/CategoryPickerPanel";
import { SidePanel } from "../../components/SidePanel";
import { TagPickerPanel } from "../../components/TagPickerPanel";
import { financeApi } from "../../lib/api";
import { appToday } from "../../lib/devDate";
import { dateInputValue } from "../../lib/format";
import { queryKeys } from "../../lib/queryKeys";
import type { Account, Category, Currency, Group, Member, Tag, TransactionType } from "../../lib/types";
import { LinkedTransferPicker } from "./LinkedTransferPicker";
import { RecordEditorActions } from "./RecordEditorActions";
import { RecordEditorFields } from "./RecordEditorFields";
import {
  buildRecordPayload,
  hasRecordErrors,
  recordDateLabel,
  type RecordEditorState,
  type RecordFormErrors,
  validateRecordForm,
} from "./recordForm";
import { useRecordEditorMutations } from "./useRecordEditorMutations";

export function RecordEditorPanel({
  state,
  group,
  accounts,
  members,
  categories,
  tags,
  currencies,
  onClose,
}: {
  state: RecordEditorState;
  group: Group;
  accounts: Account[];
  members: Member[];
  categories: Category[];
  tags: Tag[];
  currencies: Currency[];
  onClose: () => void;
}) {
  const transaction = state.kind === "edit" ? state.transaction : null;
  const isArchived = Boolean(transaction?.is_archived);
  const [type, setType] = useState<TransactionType>(transaction?.type || (state.kind === "create" ? state.initialType : undefined) || "expense");
  const [categoryId, setCategoryId] = useState(transaction?.category_id || "");
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>(transaction?.tags?.map((tag) => tag.id) || []);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [occurredDate, setOccurredDate] = useState(() => dateInputValue(transaction?.occurred_at || appToday()));
  const activeAccounts = accounts.filter((account) => !account.is_archived || account.id === transaction?.account_id);
  const initialAccountId = transaction?.account_id || activeAccounts[0]?.id || "";
  const [accountId, setAccountId] = useState(initialAccountId);
  const selectedAccount = accounts.find((account) => account.id === accountId);
  const accountCurrency = selectedAccount?.currency || group.base_currency || "EUR";
  const initialCurrency = transaction?.original_currency || transaction?.currency || accountCurrency;
  const [amount, setAmount] = useState(transaction?.original_amount || transaction?.amount || "");
  const [currency, setCurrency] = useState(initialCurrency);
  const [accountAmount, setAccountAmount] = useState(transaction?.amount || "");
  const [accountAmountEdited, setAccountAmountEdited] = useState(Boolean(transaction && initialCurrency !== accountCurrency));
  const [debouncedConversion, setDebouncedConversion] = useState({
    amount: transaction?.original_amount || transaction?.amount || "",
    currency: initialCurrency,
    accountCurrency,
    occurredDate,
  });
  const [status, setStatus] = useState("");
  const [fieldErrors, setFieldErrors] = useState<RecordFormErrors>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const selectedCategory = categories.find((category) => category.id === categoryId);
  const selectedTags = useMemo(
    () => tagIds.map((tagId) => tags.find((tag) => tag.id === tagId)).filter((tag): tag is Tag => Boolean(tag)),
    [tagIds, tags],
  );
  const isCorrection = type === "correction";
  const popularTagsQuery = useQuery({
    queryKey: queryKeys.popularTags(group.id, categoryId),
    queryFn: () => financeApi.popularTags(group.id, categoryId),
    enabled: Boolean(categoryId) && !isArchived && type !== "transfer" && !isCorrection,
  });
  const popularTagSuggestions = (popularTagsQuery.data || []).filter((tag) => !tagIds.includes(tag.id)).slice(0, 5);
  const amountNumber = Number(String(amount || "").replace(",", "."));
  const needsConversion = Boolean(currency && accountCurrency && currency !== accountCurrency);
  const debouncedAmountNumber = Number(String(debouncedConversion.amount || "").replace(",", "."));
  const canConvertCurrent = needsConversion && Boolean(occurredDate) && Number.isFinite(amountNumber) && amountNumber > 0;
  const isConversionDebouncing =
    canConvertCurrent &&
    (debouncedConversion.amount !== amount ||
      debouncedConversion.currency !== currency ||
      debouncedConversion.accountCurrency !== accountCurrency ||
      debouncedConversion.occurredDate !== occurredDate);
  const conversionQuery = useQuery({
    queryKey: queryKeys.currencyConversion(group.id, debouncedConversion.amount, debouncedConversion.currency, debouncedConversion.accountCurrency, debouncedConversion.occurredDate),
    queryFn: () => financeApi.convertCurrency(group.id, {
      amount: Number.isFinite(debouncedAmountNumber) ? debouncedAmountNumber.toFixed(2) : "0",
      base: debouncedConversion.currency,
      quote: debouncedConversion.accountCurrency,
      date: debouncedConversion.occurredDate,
    }),
    enabled:
      debouncedConversion.currency !== debouncedConversion.accountCurrency &&
      Boolean(debouncedConversion.occurredDate) &&
      Number.isFinite(debouncedAmountNumber) &&
      debouncedAmountNumber > 0,
  });
  const linkCandidatesQuery = useQuery({
    queryKey: queryKeys.linkCandidates(group.id, transaction?.id),
    queryFn: () => financeApi.linkCandidates(group.id, transaction!.id),
    enabled: linkPickerOpen && Boolean(transaction),
  });
  const conversionSourceInitializedRef = useRef(false);
  const animatedCloseRef = useRef(onClose);
  const requestAnimatedClose = () => animatedCloseRef.current();
  const mutations = useRecordEditorMutations({ group, transaction, onClose, requestAnimatedClose, setStatus });

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedConversion({ amount, currency, accountCurrency, occurredDate }), 2000);
    return () => window.clearTimeout(timer);
  }, [amount, currency, accountCurrency, occurredDate]);

  useEffect(() => {
    if (!conversionSourceInitializedRef.current) {
      conversionSourceInitializedRef.current = true;
      return;
    }
    setAccountAmount("");
    setAccountAmountEdited(false);
  }, [amount, currency, accountCurrency, occurredDate]);

  useEffect(() => {
    if (!accountAmountEdited && conversionQuery.data?.converted_amount && !isConversionDebouncing) {
      setAccountAmount(conversionQuery.data.converted_amount);
    }
  }, [accountAmountEdited, conversionQuery.data?.converted_amount, isConversionDebouncing]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isArchived) return;
    setStatus("");
    const errors = validateRecordForm(event.currentTarget, type);
    setFieldErrors(errors);
    if (hasRecordErrors(errors)) return;
    const { payload, error } = buildRecordPayload(event.currentTarget, type, categoryId, tagIds);
    if (error) {
      setStatus(error);
      return;
    }
    mutations.saveMutation.mutate(payload!);
  };
  const setDateShortcut = (offsetDays: number) => {
    const date = appToday();
    date.setDate(date.getDate() + offsetDays);
    setOccurredDate(dateInputValue(date));
    setFieldErrors((current) => ({ ...current, occurred_date: undefined }));
  };
  const todayValue = dateInputValue(appToday());
  const yesterday = appToday();
  yesterday.setDate(yesterday.getDate() - 1);
  const canRestoreRecurringDueDate = Boolean(transaction?.recurring_payment_id && transaction.recurring_due_date && !isArchived);

  return (
    <SidePanel ariaLabelledBy="recordModalTitle" className="record-modal" onClose={onClose}>
      {({ close }) => {
        animatedCloseRef.current = close;
        return (
          <>
            <Box className="records-modal-toolbar">
              <h2 id="recordModalTitle">
                {isArchived ? "Deleted record" : transaction ? "Edit record" : "Add record"}
                {transaction?.import_id ? (
                  <Tooltip title="This record was created from an import.">
                    <FileSpreadsheet className="record-import-icon" size={18} aria-label="Imported record" />
                  </Tooltip>
                ) : null}
                {transaction?.recurring_payment_id ? (
                  <Tooltip title="This record was created from a recurring payment.">
                    <Repeat className="record-import-icon" size={18} aria-label="Recurring payment record" />
                  </Tooltip>
                ) : null}
              </h2>
              <IconButton className="records-icon-button" type="button" aria-label="Close record dialog" title="Close" onClick={close}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </IconButton>
            </Box>
            <form className="records-form" noValidate onSubmit={submit}>
              <RecordEditorFields
                transaction={transaction} accounts={accounts} activeAccounts={activeAccounts} members={members} currencies={currencies}
                type={type} setType={setType} isArchived={isArchived} isCorrection={isCorrection}
                categoryId={categoryId} setCategoryId={setCategoryId} selectedCategory={selectedCategory}
                selectedTags={selectedTags} popularTagSuggestions={popularTagSuggestions} setTagIds={setTagIds}
                setCategoryPickerOpen={setCategoryPickerOpen} setTagPickerOpen={setTagPickerOpen}
                occurredDate={occurredDate} setOccurredDate={setOccurredDate} todayValue={todayValue}
                yesterdayValue={dateInputValue(yesterday)} setDateShortcut={setDateShortcut}
                amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency}
                accountId={accountId} setAccountId={setAccountId} accountCurrency={accountCurrency}
                accountAmount={accountAmount} setAccountAmount={setAccountAmount}
                accountAmountEdited={accountAmountEdited} setAccountAmountEdited={setAccountAmountEdited}
                needsConversion={needsConversion} canConvertCurrent={canConvertCurrent}
                isConversionDebouncing={isConversionDebouncing} conversionError={conversionQuery.isError}
                conversion={conversionQuery.data} fieldErrors={fieldErrors} setFieldErrors={setFieldErrors}
              />
              <RecordEditorActions
                transaction={transaction} isArchived={isArchived} confirmDelete={confirmDelete}
                canRestoreRecurringDueDate={canRestoreRecurringDueDate}
                recurringDueDateLabel={recordDateLabel(transaction?.recurring_due_date)}
                pending={{
                  save: mutations.saveMutation.isPending,
                  delete: mutations.deleteMutation.isPending,
                  restore: mutations.restoreMutation.isPending,
                  unlink: mutations.unlinkMutation.isPending,
                }}
                onCancelDelete={() => setConfirmDelete(false)}
                onDelete={(restoreDueDate) => {
                  if (!confirmDelete) setConfirmDelete(true);
                  else mutations.deleteMutation.mutate(Boolean(restoreDueDate));
                }}
                onRestore={() => mutations.restoreMutation.mutate()}
                onUnlink={() => mutations.unlinkMutation.mutate()}
                onOpenLinkPicker={() => setLinkPickerOpen(true)}
              />
            </form>
            {status ? <p className="status error">{status}</p> : null}
            {categoryPickerOpen ? (
              <CategoryPickerPanel
                group={group} type={type} categories={categories} selectedCategoryId={categoryId}
                onApply={setCategoryId} onClose={() => setCategoryPickerOpen(false)}
                onCancelAll={() => { setCategoryPickerOpen(false); requestAnimatedClose(); }}
              />
            ) : null}
            {tagPickerOpen ? (
              <TagPickerPanel
                group={group} categoryId={type === "transfer" || isCorrection ? "" : categoryId}
                selectedTagIds={tagIds} onApply={setTagIds} onClose={() => setTagPickerOpen(false)}
                onCancelAll={() => { setTagPickerOpen(false); requestAnimatedClose(); }}
              />
            ) : null}
            {linkPickerOpen ? (
              <LinkedTransferPicker
                accounts={accounts} candidates={linkCandidatesQuery.data || []} isLoading={linkCandidatesQuery.isLoading}
                linkMutation={{
                  isPending: mutations.linkMutation.isPending,
                  mutate: (partnerId) => mutations.linkMutation.mutate(partnerId, { onSuccess: () => setLinkPickerOpen(false) }),
                }}
                onBack={() => setLinkPickerOpen(false)} onCloseAll={requestAnimatedClose}
              />
            ) : null}
          </>
        );
      }}
    </SidePanel>
  );
}
